import React, { createContext, useContext, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "./AuthContext";

const ConsultationContext = createContext(null);

export function ConsultationProvider({ children }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const saveExpertNotes = useCallback(async (notesData) => {
    if (!user) return null;

    const token = localStorage.getItem("obstuchim_token");
    if (token && notesData.booking_id) {
      try {
        const res = await fetch(`/api/consultations/${notesData.booking_id}/expert-notes`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            summary: notesData.summary,
            recommendations: notesData.recommendations,
            homework: notesData.homework || "",
            privateNote: notesData.private_note || "",
            nextFocus: notesData.next_focus || "",
          }),
        });
        if (res.ok) {
          const json = await res.json();
          return json.consultation?.expertNotes || json.consultation || true;
        }
      } catch (apiErr) {
        console.warn("[saveExpertNotes] API save failed, trying fallback:", apiErr);
      }
    }

    const { data: existing } = await supabase
      .from("consultation_notes")
      .select("id")
      .eq("booking_id", notesData.booking_id)
      .maybeSingle();

    if (existing) {
      const { data, error } = await supabase
        .from("consultation_notes")
        .update({
          summary: notesData.summary,
          recommendations: notesData.recommendations,
          homework: notesData.homework || "",
          private_note: notesData.private_note || "",
          next_focus: notesData.next_focus || "",
          updated_at: new Date().toISOString()
        })
        .eq("id", existing.id)
        .select()
        .single();
      return error ? null : data;
    }

    const { data, error } = await supabase
      .from("consultation_notes")
      .insert({
        booking_id: notesData.booking_id,
        expert_id: notesData.expert_id,
        client_id: notesData.client_id,
        expert_name: notesData.expert_name || "",
        client_name: notesData.client_name || "",
        session_date: notesData.session_date || "",
        session_topic: notesData.session_topic || "",
        summary: notesData.summary,
        recommendations: notesData.recommendations,
        homework: notesData.homework || "",
        private_note: notesData.private_note || "",
        next_focus: notesData.next_focus || ""
      })
      .select()
      .single();
    return error ? null : data;
  }, [user]);

  const getExpertNotes = useCallback(async (bookingId) => {
    const token = localStorage.getItem("obstuchim_token");
    if (token && bookingId) {
      try {
        const res = await fetch(`/api/consultations/${bookingId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const json = await res.json();
          if (json.consultation?.expertNotes) {
            const n = json.consultation.expertNotes;
            return {
              booking_id: bookingId,
              summary: n.summary,
              recommendations: n.recommendations,
              homework: n.homework,
              private_note: n.privateNote,
              next_focus: n.nextFocus,
            };
          }
        }
      } catch (apiErr) {
        console.warn("[getExpertNotes] API fetch failed, trying fallback:", apiErr);
      }
    }

    const { data } = await supabase
      .from("consultation_notes")
      .select("*")
      .eq("booking_id", bookingId)
      .maybeSingle();
    return data;
  }, []);

  const getNotesForExpert = useCallback(async (expertId) => {
    const { data } = await supabase
      .from("consultation_notes")
      .select("*")
      .eq("expert_id", expertId)
      .order("created_at", { ascending: false });
    return data || [];
  }, []);

  const getNotesForClient = useCallback(async (clientId) => {
    const { data } = await supabase
      .from("consultation_notes")
      .select("*")
      .eq("client_id", clientId)
      .order("created_at", { ascending: false });
    return data || [];
  }, []);

  const saveClientNote = useCallback(async (noteData) => {
    if (!user) return null;

    const token = localStorage.getItem("obstuchim_token");
    if (token && noteData.booking_id) {
      try {
        const res = await fetch(`/api/consultations/${noteData.booking_id}/prep-brief`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            insights: noteData.insights || "",
            actionPlan: noteData.action_plan || "",
            nextQuestions: noteData.next_questions || "",
          }),
        });
        if (res.ok) {
          const json = await res.json();
          return json.consultation?.clientPrepBrief || true;
        }
      } catch (apiErr) {
        console.warn("[saveClientNote] API save failed, trying fallback:", apiErr);
      }
    }

    const { data: existing } = await supabase
      .from("client_notes")
      .select("id")
      .eq("booking_id", noteData.booking_id)
      .maybeSingle();

    if (existing) {
      const { data, error } = await supabase
        .from("client_notes")
        .update({
          insights: noteData.insights || "",
          action_plan: noteData.action_plan || "",
          next_questions: noteData.next_questions || "",
          updated_at: new Date().toISOString()
        })
        .eq("id", existing.id)
        .select()
        .single();
      return error ? null : data;
    }

    const { data, error } = await supabase
      .from("client_notes")
      .insert({
        booking_id: noteData.booking_id,
        client_id: noteData.client_id,
        expert_id: noteData.expert_id,
        insights: noteData.insights || "",
        action_plan: noteData.action_plan || "",
        next_questions: noteData.next_questions || ""
      })
      .select()
      .single();
    return error ? null : data;
  }, [user]);

  const getClientNote = useCallback(async (bookingId) => {
    const token = localStorage.getItem("obstuchim_token");
    if (token && bookingId) {
      try {
        const res = await fetch(`/api/consultations/${bookingId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const json = await res.json();
          if (json.consultation?.clientPrepBrief) {
            const b = json.consultation.clientPrepBrief;
            return {
              booking_id: bookingId,
              insights: b.insights || "",
              action_plan: b.actionPlan || "",
              next_questions: b.nextQuestions || "",
            };
          }
        }
      } catch (apiErr) {
        console.warn("[getClientNote] API fetch failed, trying fallback:", apiErr);
      }
    }

    const { data } = await supabase
      .from("client_notes")
      .select("*")
      .eq("booking_id", bookingId)
      .maybeSingle();
    return data;
  }, []);

  const addGoal = useCallback(async (expertId, clientId, title) => {
    if (!user) return null;
    const { data: existing } = await supabase
      .from("consultation_goals")
      .select("sort_order")
      .eq("expert_id", expertId)
      .eq("client_id", clientId)
      .order("sort_order", { ascending: false })
      .limit(1);

    const nextOrder = existing && existing.length > 0
      ? (existing[0].sort_order || 0) + 1
      : 0;

    const { data, error } = await supabase
      .from("consultation_goals")
      .insert({
        expert_id: expertId,
        client_id: clientId,
        title,
        sort_order: nextOrder
      })
      .select()
      .single();
    return error ? null : data;
  }, [user]);

  const toggleGoal = useCallback(async (goalId, isCompleted) => {
    const { data, error } = await supabase
      .from("consultation_goals")
      .update({
        is_completed: isCompleted,
        completed_at: isCompleted ? new Date().toISOString() : null
      })
      .eq("id", goalId)
      .select()
      .single();
    return error ? null : data;
  }, []);

  const deleteGoal = useCallback(async (goalId) => {
    const { error } = await supabase
      .from("consultation_goals")
      .delete()
      .eq("id", goalId);
    return !error;
  }, []);

  const getGoals = useCallback(async (expertId, clientId) => {
    const { data } = await supabase
      .from("consultation_goals")
      .select("*")
      .eq("expert_id", expertId)
      .eq("client_id", clientId)
      .order("sort_order", { ascending: true });
    return data || [];
  }, []);

  const getGoalsForClient = useCallback(async (clientId) => {
    const { data } = await supabase
      .from("consultation_goals")
      .select("*")
      .eq("client_id", clientId)
      .order("sort_order", { ascending: true });
    return data || [];
  }, []);

  return (
    <ConsultationContext.Provider
      value={{
        loading,
        saveExpertNotes,
        getExpertNotes,
        getNotesForExpert,
        getNotesForClient,
        saveClientNote,
        getClientNote,
        addGoal,
        toggleGoal,
        deleteGoal,
        getGoals,
        getGoalsForClient
      }}
    >
      {children}
    </ConsultationContext.Provider>
  );
}

export function useConsultation() {
  const context = useContext(ConsultationContext);
  if (!context) {
    throw new Error("useConsultation must be used within a ConsultationProvider");
  }
  return context;
}

